import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Wind, Flame, Wrench, Leaf, CalendarCheck } from "lucide-react";
import Reveal from "@/components/Reveal";
import PartnersMarquee from "@/components/PartnersMarquee";
import HealthCheck from "@/components/HealthCheck";
import TwoPaths from "@/components/TwoPaths";
import GoogleBusinessSection from "@/components/GoogleBusinessSection";
import Seo from "@/components/Seo";
import { SERVICES } from "@/data/services";
import { COMMON_ISSUES } from "@/data/industries";

const Stat = ({ value, label, color = "text-brand" }) => (
  <div>
    <div className={`font-mono ${color} text-[3rem] sm:text-[3.5rem] leading-none tracking-tightest font-medium`}>
      {value}
    </div>
    <div className="mt-3 text-[13px] text-muted-foreground tracking-wide uppercase font-mono">
      {label}
    </div>
  </div>
);

const Home = ({ onBook }) => {
  React.useEffect(() => {
    if (!window.location.hash) return;
    const t = setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <Seo
        title="Commercial HVAC West London — Calmbreeze"
        description="Calmbreeze is a West London commercial HVAC specialist. AHU maintenance London, fire damper drop testing London, TR19 duct cleaning, F-Gas engineering, MVHR maintenance and TM44 inspection — all under one accountable partner."
        path="/"
      />
      {/* HERO */}
      <section
        className="pt-[120px] md:pt-[160px] pb-24 relative overflow-hidden bg-gradient-to-b from-paper-cream via-white to-white"
        data-testid="hero-section"
      >
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6 flex items-center gap-3">
                <span className="inline-block w-6 h-px bg-accent" />
                [ HVAC · Compliance · Indoor Air Quality ]
              </div>
              <h1 className="font-display font-bold text-[3rem] sm:text-[4.5rem] lg:text-[6rem] leading-[0.95] tracking-tightest text-ink">
                Clean air,<br />
                <span className="text-brand">calm spaces.</span>
              </h1>
              <p className="mt-8 text-[18px] sm:text-[20px] text-muted-foreground max-w-xl leading-relaxed">
                <span className="font-serif italic text-ink/70">A West London commercial HVAC partner.</span><br />
                AHU maintenance, fire damper drop testing, TR19 kitchen extract cleaning, F-Gas, MVHR and TM44 inspection — covered by one accountable team across London.
              </p>
              <div className="mt-4 font-mono text-[11px] tracking-overline uppercase text-muted">
                Founded &amp; run by{" "}
                <Link to="/about#meet-stevie" data-testid="hero-founder-link" className="text-brand hover:text-accent transition-colors">
                  Stevie Coles
                </Link>{" "}
                · 11+ yrs in commercial HVAC
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onBook}
                  data-testid="hero-cta-book"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors"
                >
                  Book a free site survey
                  <CalendarCheck className="h-4 w-4" />
                </button>
                <a
                  href="#health-check"
                  data-testid="hero-cta-healthcheck"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-accent text-white font-semibold text-[14px] rounded-sm hover:bg-brand transition-colors"
                >
                  60-second health check
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-md overflow-hidden">
                <img
                  src="/hero/atrium.jpg"
                  alt="Modern architectural atrium"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur-sm rounded-sm p-4 border border-black/5">
                  <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-1">
                    Featured · case study
                  </div>
                  <div className="font-display font-semibold text-[15px] leading-snug">
                    University of Reading — <span className="text-accent">560 fire dampers</span> across 77 buildings.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stat ribbon */}
          <div className="mt-24 pt-12 relative">
            <div className="absolute top-0 left-0 right-0 flex items-center gap-4">
              <span className="h-px bg-black/8 flex-1" />
              <span className="font-mono text-[10px] tracking-overline uppercase text-gilded">
                ✦ By appointment · West London ✦
              </span>
              <span className="h-px bg-black/8 flex-1" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <Stat value="2022" label="Founded · West London" color="text-ink" />
              <Stat value="560+" label="Fire dampers tested" color="text-accent" />
              <Stat value="77" label="Buildings surveyed" color="text-brand" />
              <Stat value="100%" label="Compliance focus" color="text-ink" />
            </div>
          </div>
        </div>
      </section>

      <TwoPaths onBook={onBook} />

      {/* SERVICES BENTO */}
      <section className="py-24 md:py-32 bg-paper-cool" data-testid="services-overview">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">
                [ 01 — Services ]
              </div>
              <h2 className="font-display font-bold text-[2.5rem] md:text-[3.5rem] leading-[1.02] tracking-tightest">
                Six disciplines.<br />
                <span className="text-brand">One clean-air partner.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[16px] text-muted-foreground leading-relaxed">
                From AHU servicing and refrigeration compliance to fire damper drop testing
                and TR19 kitchen extract cleaning — we cover the full mechanical envelope of your building.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal
                key={s.slug}
                delay={i * 80}
                className={`${s.span} col-span-1`}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block h-full bg-white border border-black/5 rounded-md p-8 md:p-10 hover:border-black/15 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-start justify-between gap-4 mb-8">
                    <div className="font-mono text-[11px] tracking-overline uppercase text-muted">
                      {s.overline}
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-ink/40 group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <h3 className="font-display font-semibold text-[1.5rem] md:text-[1.75rem] leading-tight tracking-tight text-ink mb-4 group-hover:text-brand transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-[14px] text-muted-foreground leading-relaxed">
                    {s.short}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDY */}
      <section className="py-24 md:py-32 bg-paper" data-testid="featured-case-study">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="aspect-[5/4] rounded-md overflow-hidden">
                <img
                  src="/hero/engineer-onsite.jpg"
                  alt="Engineer on site"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-4">
                [ Case Study · 2024 ]
              </div>
              <h2 className="font-display font-bold text-[2rem] md:text-[3rem] leading-[1.05] tracking-tightest mb-6">
                The University of Reading,<br />
                <span className="text-brand">end-to-end fire damper compliance.</span>
              </h2>
              <p className="text-[16px] text-muted-foreground leading-relaxed mb-10 max-w-lg">
                Three campuses, term-time delivery, zero academic disruption.
                We surveyed, mapped and tested fire dampers across the entire university estate
                with live progress reporting through Smartsheet dashboards.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-10">
                <div>
                  <div className="font-mono text-accent text-[2.5rem] leading-none font-medium tracking-tightest">77</div>
                  <div className="text-[11px] tracking-overline uppercase text-muted font-mono mt-2">Buildings</div>
                </div>
                <div>
                  <div className="font-mono text-accent text-[2.5rem] leading-none font-medium tracking-tightest">560</div>
                  <div className="text-[11px] tracking-overline uppercase text-muted font-mono mt-2">Dampers</div>
                </div>
                <div>
                  <div className="font-mono text-accent text-[2.5rem] leading-none font-medium tracking-tightest">1,792</div>
                  <div className="text-[11px] tracking-overline uppercase text-muted font-mono mt-2">Hours</div>
                </div>
              </div>
              <Link
                to="/case-studies"
                data-testid="case-study-cta"
                className="inline-flex items-center gap-2 group"
              >
                <span className="font-semibold text-ink border-b border-ink/30 pb-1 group-hover:border-ink transition-colors">
                  Read the full case study
                </span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <PartnersMarquee />

      {/* COMMON ISSUES */}
      <section className="py-24 md:py-32 bg-paper" data-testid="common-issues">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-4">
                [ 02 — Common issues we solve ]
              </div>
              <h2 className="font-display font-bold text-[2.25rem] md:text-[3.25rem] leading-[1.03] tracking-tightest">
                If any of this sounds familiar —{" "}
                <span className="text-brand">we can help.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-3">
              <p className="text-[16px] text-muted-foreground leading-relaxed">
                These are the things we hear most from facilities managers, business owners and operators.
                Our job is to take them off your desk for good.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
            {COMMON_ISSUES.map((item, i) => (
              <Reveal key={i} delay={i * 40} className="border-t border-black/8 py-7">
                <div className="flex items-start gap-5">
                  <div className="font-mono text-accent text-[12px] mt-1 tracking-overline">{String(i + 1).padStart(2, "0")}</div>
                  <div className="flex-1">
                    <h4 className="font-display font-semibold text-[1.05rem] tracking-tight text-ink mb-2">
                      {item.problem}
                    </h4>
                    <p className="text-[14px] text-muted-foreground leading-relaxed">
                      {item.solution}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HEALTH CHECK */}
      <HealthCheck onBook={onBook} />

      {/* GOOGLE BUSINESS PROFILE */}
      <GoogleBusinessSection />

      {/* WHY US */}
      <section className="py-24 md:py-32 bg-ink text-white" data-testid="why-calmbreeze">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-4">
                [ 03 — Why Calmbreeze ]
              </div>
              <h2 className="font-display font-bold text-[2.25rem] md:text-[3.25rem] leading-[1.03] tracking-tightest text-white">
                A proactive partner —{" "}
                <span className="text-accent">not another contractor.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Wind, title: "Proactive, not reactive", text: "Issues caught before they cause downtime, not after they cost you a service." },
              { icon: Flame, title: "Compliance, hassle-free", text: "Fire dampers, TR19, F-Gas. Documented, certified, and ready for audit." },
              { icon: Wrench, title: "One partner, full envelope", text: "AHU, AC, kitchen extract, steam, MVHR and fire — all under one roof." },
              { icon: Leaf, title: "Sustainable & efficient", text: "Energy-aware servicing aligned with Net Zero by 2050 (PPN 06/21)." },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={i} delay={i * 80} className="border-t border-white/15 pt-8">
                  <Icon className="h-6 w-6 text-brand mb-6" strokeWidth={1.6} />
                  <h4 className="font-display font-semibold text-[1.15rem] tracking-tight text-white mb-3">
                    {p.title}
                  </h4>
                  <p className="text-[14px] text-white/60 leading-relaxed">{p.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
